<template>
  <Teleport to="body">
    <v-navigation-drawer
      :model-value="modelValue"
      location="right"
      temporary
      :width="drawerWidth"
      class="cr-profile-drawer"
      @update:model-value="onDrawerUpdate"
    >
      <div v-if="caller" class="cr-profile-drawer__inner">
        <header class="cr-profile-header">
          <div class="cr-profile-header__brand">
            <div class="cr-profile-header__icon" aria-hidden="true">
              <v-icon icon="mdi-account-voice" size="22" color="white" />
            </div>
            <div class="cr-profile-header__titles">
              <h2 class="cr-profile-header__title">Caller profile</h2>
              <p class="cr-profile-header__subtitle">
                {{ caller.caller_id_display || caller.caller_id }}
              </p>
            </div>
          </div>
          <button
            type="button"
            class="cr-profile-header__close"
            aria-label="Close"
            @click="close"
          >
            <v-icon icon="mdi-close" size="20" />
          </button>
        </header>

        <div class="cr-profile-body">
          <section class="cr-profile-card">
            <h3 class="cr-profile-card__label">Profile overview</h3>
            <dl class="cr-profile-overview">
              <div class="cr-profile-overview__item">
                <dt>Caller name</dt>
                <dd>{{ nameLabel }}</dd>
              </div>
              <div class="cr-profile-overview__item">
                <dt>Contact number</dt>
                <dd>{{ caller.caller_id_display || caller.caller_id }}</dd>
              </div>
              <div class="cr-profile-overview__item">
                <dt>Last called</dt>
                <dd>{{ lastCalledLabel }}</dd>
              </div>
              <div class="cr-profile-overview__item">
                <dt>Total calls</dt>
                <dd>{{ callsLabel }}</dd>
              </div>
            </dl>
          </section>

          <div class="cr-profile-actions">
            <v-btn
              variant="flat"
              class="cr-profile-actions__edit"
              prepend-icon="mdi-account-edit-outline"
              @click="$emit('edit', caller)"
            >
              Edit caller
            </v-btn>
            <v-btn
              variant="outlined"
              class="cr-profile-actions__delete"
              prepend-icon="mdi-trash-can-outline"
              @click="$emit('delete', caller)"
            >
              Delete caller
            </v-btn>
          </div>
        </div>
      </div>
    </v-navigation-drawer>
  </Teleport>
</template>

<script setup>
import { computed, watch, onBeforeUnmount } from 'vue'
import { useDisplay } from 'vuetify'
import { displayCallerName } from '@/composables/callers/useCallersData'
import { formatDateTime, formatNumber } from '@/utils/format'

defineOptions({ name: 'CallerProfileDrawer' })

const PROFILE_DRAWER_OPEN_CLASS = 'cr-profile-drawer-open'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  caller: { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue', 'edit', 'delete'])

const display = useDisplay()

const drawerWidth = computed(() =>
  display.mobile.value ? '100%' : Math.min(440, display.width.value),
)

const nameLabel = computed(() => displayCallerName(props.caller?.caller_name))
const lastCalledLabel = computed(() => formatDateTime(props.caller?.last_call_on))
const callsLabel = computed(() => formatNumber(props.caller?.total_calls))

function setDrawerOpenState(open) {
  const root = document.documentElement
  if (!root) return
  if (open) root.classList.add(PROFILE_DRAWER_OPEN_CLASS)
  else root.classList.remove(PROFILE_DRAWER_OPEN_CLASS)
}

function close() {
  emit('update:modelValue', false)
}

function onDrawerUpdate(value) {
  emit('update:modelValue', value)
}

watch(
  () => props.modelValue,
  (open) => setDrawerOpenState(open),
  { immediate: true },
)

onBeforeUnmount(() => setDrawerOpenState(false))
</script>
